import { useJobillaOrgId } from '@/hooks'
import { Button, LibraryDialog, LibraryLayout } from '@/ui'
import type { AdDesignData } from '@jobilla/nexus-sdk'
import { useAdDesignIndex } from '@jobilla/nexus-sdk'
import { useAtom } from 'jotai'
import { useCallback, useState } from 'react'
import { designGalleryOpenAtom, designGalleryPromiseAtom } from '../atoms'
import { useDesignGalleryStore } from '../store'
import { GalleryGrid } from './gallery-grid'

export function DesignGalleryPicker() {
	const store = useDesignGalleryStore()
	const [isOpen, setIsOpen] = useAtom(designGalleryOpenAtom, { store })
	const [promise, setPromise] = useAtom(designGalleryPromiseAtom, { store })

	const handleClose = useCallback(() => {
		promise?.resolve(undefined)
		setPromise(null)
		setIsOpen(false)
	}, [promise, setPromise, setIsOpen])

	const handleSelect = useCallback((design: AdDesignData) => {
		promise?.resolve(design)
		setPromise(null)
		setIsOpen(false)
	}, [promise, setPromise, setIsOpen])

	return (
		<LibraryDialog open={isOpen} onClose={handleClose}>
			{isOpen
				? <DesignGalleryPickerContent onSelect={handleSelect} onClose={handleClose} />
				: null}
		</LibraryDialog>
	)
}

function DesignGalleryPickerContent({
	onSelect,
	onClose,
}: {
	onSelect: (design: AdDesignData) => void
	onClose: () => void
}) {
	const organisationId = useJobillaOrgId()
	const { data: designs, isLoading } = useAdDesignIndex(organisationId ?? '')
	const [selectedDesign, setSelectedDesign] = useState<AdDesignData | null>(null)

	return (
		<LibraryLayout
			title="Select a Design"
			showCloseButton={true}
			onClose={onClose}
			isLoading={isLoading}
			committalBar={
				<div className="flex justify-end p-4">
					<Button
						size="normal"
						type="committal"
						onClick={() => selectedDesign && onSelect(selectedDesign)}
						disabled={!selectedDesign}
					>
						Select Design
					</Button>
				</div>
			}
		>
			<div className="p-4">
				<GalleryGrid
					designs={designs ?? []}
					selectedId={selectedDesign?.id}
					onSelect={setSelectedDesign}
					showActions={false}
					emptyState={
						<div className="flex items-center justify-center h-48 text-sm text-grey-400">
							No designs saved yet. Save a design from the Design Simulator first.
						</div>
					}
				/>
			</div>
		</LibraryLayout>
	)
}
